// The two scoped packages the `firebase` 6 umbrella re-exported. The umbrella also pulled in
// Firestore and its native `grpc` dependency, which does not build on Node 22; firebase 7+
// messaging needs apiKey/projectId/appId that this portal does not configure (PANEL-1525).
import firebase from '@firebase/app'
import '@firebase/messaging'
import { isMobile } from 'react-device-detect'
import md5 from 'md5'
import urlConfig from './utils/config'

const NOTIFICATION_TOKEN_KEY = 'notification-token'

export const initializeFirebase = (sender_id) => {
	const config = {
		messagingSenderId: sender_id
	};
	if (!firebase.apps.length) {
		firebase.initializeApp(config);
	}
};

export const sendTokenToServer = (REGISTRATION_TOKEN, AUTH_TOKEN, panel_name) => {
	const data = {
		token: REGISTRATION_TOKEN,
		type: isMobile ? 'mobile' : 'desktop',
	}
	return fetch(`${urlConfig.generateUrl(panel_name)}/tgm-api/push/add`, {
	  method: 'POST',
	  headers: new Headers({
		'Authorization': 'Bearer ' + AUTH_TOKEN,
		'Content-Type': 'application/json'
	  }),
	  body: JSON.stringify(data)
	}).then(res => {
	  // Every response used to count as a save, so a 401 or a 500 told the caller the server had
	  // the token when it did not. Callers are `.then`-only and one of them runs inside an FCM
	  // service-worker callback, so a rejection here would be invisible in production: report the
	  // failure with a falsy result instead and never reject.
	  if (!res || !res.ok) {
		console.error(' - Save token to server failed - ', res && res.status);
		return null;
	  }
	  return res;
	}).catch(error => {
	  console.error(' - Save token to server Err - ', error);
	  return null;
	})
}

// The stored login user's `push_hashes` is what containers/App compares the stored token's md5
// against on every mount; it is a snapshot from login and nothing else refreshes it. A token the
// server just accepted is added to it, or every mount after a post-login subscription would see an
// unknown hash and subscribe again until the next login.
const rememberSavedToken = (token) => {
	try {
		const user = JSON.parse(localStorage.getItem('user'))
		const hashes = user && user.settings && user.settings.push_hashes
		if (!Array.isArray(hashes)) return
		const hash = md5(token)
		if (!hashes.includes(hash)) {
			hashes.push(hash)
			localStorage.setItem('user', JSON.stringify(user))
		}
	} catch (e) {
		// A missing or unreadable user record only costs one extra subscription on the next mount.
	}
}

// Stores an FCM token and saves it to the server: the one protocol the first subscription
// (containers/App/notificationModel) and onTokenRefresh share. Resolves true when the server has
// the token, false when it refused it; rejects when the save could not even be issued. On both
// failures the token is dropped again, so the next mount re-subscribes instead of trusting a token
// the server never received — but only the token THIS call stored: a concurrent refresh may
// already have stored, and saved, a newer one.
export const persistNotificationToken = (token, AUTH_TOKEN, panel_name) => {
	const dropOrphanedToken = () => {
		if (localStorage.getItem(NOTIFICATION_TOKEN_KEY) === token) {
			localStorage.removeItem(NOTIFICATION_TOKEN_KEY)
		}
	}
	let save
	try {
		// Both can throw SYNCHRONOUSLY: a storage that refuses writes (quota, blocked site data), and
		// `sendTokenToServer`, which builds the upload URL before it calls fetch, on a missing panel
		// host. Either way the caller gets a rejection, never a throw.
		localStorage.setItem(NOTIFICATION_TOKEN_KEY, token)
		save = sendTokenToServer(token, AUTH_TOKEN, panel_name)
	} catch (error) {
		save = Promise.reject(error)
	}
	return Promise.resolve(save).then(saveRes => {
		if (!saveRes) {
			dropOrphanedToken()
			return false
		}
		rememberSavedToken(token)
		return true
	}, error => {
		dropOrphanedToken()
		throw error
	})
}

export const askForPermissionToReceiveNotifications = async (AUTH_TOKEN, panel_name) => {
	try{
		const messaging = firebase.messaging();
		await messaging.requestPermission();
		const token = await messaging.getToken();
		messaging.onMessage(payload => {
			console.log('[message Received]');
			const notification =  payload.notification;
			if (notification) {
				const notificationTitle = notification.title || "Notification";
				navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js').then((registration) => {
					if(registration){
						registration.showNotification(notificationTitle, notification); 
					}
				});
			}
		});

		messaging.onTokenRefresh(() => {
			messaging.getToken().then((refreshedToken) => {
				if (refreshedToken) {
					// Returned, so a save that could not be issued reaches the `.catch` below.
					return persistNotificationToken(refreshedToken, AUTH_TOKEN, panel_name).then(saved => {
						if (saved) {
							console.log('[ Notification token refreshed ]');
						}
					});
				} else {
					// Show permission request.
					// The old token is no longer valid; drop it so the app re-subscribes
					// instead of treating a stale (or "null") value as a live token.
					localStorage.removeItem(NOTIFICATION_TOKEN_KEY);
					console.log('No Instance ID token available. Request permission to generate one.');
				}
			}).catch((err) => {
				console.log('Unable to retrieve refreshed token ', err);
			});
		});

		return token;
	} catch (error){
		console.log(error);
	}
};